import React, { useEffect, useState } from "react";
import { Box, CircularProgress, Stack, Typography } from "@mui/joy";
import { theme } from "styles/theme";
import CustomTypography from "components/common/CustomTypography";
import { BottomNextButton } from "components/common/BottomNextButton";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  ONBOARDING_ANALYSIS_DETAIL_ENDPOINT,
  SurveyAnalysisDetailItem,
} from "api/onboardingApi";
import { useNavigate } from "react-router-dom";
import ContentTemplateV2 from "../../../../components/task/content/ContentTemplateV2";
import useGetDeviceInfo from "../../../../hooks/useGetDeviceInfo";
import {
  MktVersionTypes,
  versionToPathPrefix,
} from "../../../../components/mkt/mktVersionUtils";
import useMixpanelMKT from "../../../../hooks/useMixpanelMKT";
import { WEB_FUNNEL_DOMAIN, webFunnelApi } from "../../../../api/webFunnelApi";
import { useRecoilValue } from "recoil";
import { userSimpleAtom } from "../../../../recoil/userSimpleAtom";
import useAutoLoginPromotion from "../../../../hooks/useAutoLoginPromotion";
import useFetchUserPromotion from "../../../../hooks/useFetchUserPromotion";
import useMixpanelProfileMkt from "../../../../hooks/useMixpanelProfileMkt";
import { PATH } from "../../../../routes/paths";

function renderInlineBold(text: string) {
  const nodes: React.ReactNode[] = [];
  const regex = /\*\*(.+?)\*\*/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }
    nodes.push(
      <Box
        key={`b-${match.index}-${match[1]}`}
        component="span"
        sx={{
          fontWeight: 700,
          color: theme.vars.palette.Content.Highlight,
        }}
      >
        {match[1]}
      </Box>
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }
  return nodes.length ? nodes : [text];
}

export default function Analysis_1_Promotion({
  version = "v4",
}: {
  version?: MktVersionTypes;
}) {
  const user = useRecoilValue(userSimpleAtom);
  const { isChecking } = useAutoLoginPromotion();
  const { isFetching } = useFetchUserPromotion();

  const navigate = useNavigate();

  const handleNext = () => {
    navigate(`/${versionToPathPrefix(version)}-onboarding/curriculum/1`, {
      replace: true,
    });
  };

  const pageKey = "analysis_1";

  const trackMixpanel = useMixpanelMKT({
    eventName: "설문 분석 페이지 진입",
    version: "v4",
  });

  const { mutate: trackEvent } = useMutation(
    ({
       eventName,
       properties,
     }: {
      eventName: string;
      properties?: { [key: string]: any };
    }) => webFunnelApi.trackEvent(eventName, properties)
  );

  const setPromotionPeople = useMixpanelProfileMkt("v4");

  const { homeIndicatorHeight } = useGetDeviceInfo();
  const [containerHeight] = useState<number>(
    window.innerHeight - homeIndicatorHeight
  );

  const [analysisDetail, setAnalysisDetail] = useState<
    SurveyAnalysisDetailItem[] | undefined
  >(undefined);
  const [shouldStopPolling, setShouldStopPolling] = useState(false);

  const { data: analysisData, refetch } = useQuery(
    [WEB_FUNNEL_DOMAIN, ONBOARDING_ANALYSIS_DETAIL_ENDPOINT, user?.userId],
    () => webFunnelApi.getAnalysisDetail(),
    {
      enabled: !!user?.accessToken,
      refetchInterval: shouldStopPolling ? false : 1000,
      onSuccess: (data) => {
        if (data && data.result) {
          const result = data.result;

          // analysisList가 없으면 바로 다음 페이지로 이동
          if (result.status === "completed" && !result.analysisList) {
            localStorage.setItem("ONBOARDING_TYPE", "d");
            setPromotionPeople({
              "온보딩 유형": "D",
            });
            setShouldStopPolling(true);
            navigate(`/promotion-onboarding/curriculum/1`, { replace: true });
            return;
          }

          // status가 completed이면 analysisList를 설정하고 polling 중지
          if (result.status === "completed" && result.analysisList) {
            setAnalysisDetail(result.analysisList);
            setShouldStopPolling(true);
            trackMixpanel();
            trackEvent({
              eventName: "설문 분석 페이지 진입",
              properties: {
                event_source: "web",
              },
            });
          }
        }
      },
    }
  );

  // 로그인 체크 후 리다이렉트
  useEffect(() => {
    if (!isChecking && !isFetching && !user?.accessToken) {
      navigate(PATH.promotionLogin, { replace: true });
    }
  }, [isChecking, isFetching, user, navigate]);

  const analysisSectionContent = (
    <Stack
      spacing={"40px"}
      sx={{
        width: "100%",
        maxWidth: "100%",
        px: "16px",
        pt: "12px",
        pb: "80px",
      }}
    >
      <Stack
        spacing={"16px"}
        sx={{
          width: "100%",
          maxWidth: "660px",
          textAlign: "left",
          px: "4px",
        }}
      >
        <CustomTypography
          variant={"H1"}
          sx={{
            color: theme.vars.palette.Content.Strong,
            fontWeight: 800,
          }}
        >
          {`${user?.nickname}님의 현재 위치`}
        </CustomTypography>
        <Typography
          variant={"Subtitle"}
          sx={{
            color: theme.vars.palette.Content.Medium,
            fontWeight: 500,
          }}
        >
          {"설문을 토대로 현재 나의 위치를 확인합니다."}
        </Typography>
      </Stack>

      <Stack
        spacing={"40px"}
        sx={{
          width: "100%",
          maxWidth: "660px",
        }}
      >
        {analysisDetail?.map((item, index) => (
          <Stack
            key={index}
            spacing={"20px"}
            sx={{
              px: "8px",
            }}
          >
            <Stack direction="row" spacing={"8px"} alignItems="center">
              <Box
                sx={{
                  width: "20px",
                  height: "20px",
                  borderRadius: "100px",
                  backgroundColor: theme.vars.palette.Surface.Highlight,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Typography
                  sx={{
                    color: theme.vars.palette.Content.Negative,
                    fontSize: "12px",
                    fontWeight: 700,
                  }}
                >
                  {index + 1}
                </Typography>
              </Box>
              <CustomTypography
                variant={"Body"}
                sx={{
                  p: 0,
                  color: theme.vars.palette.Content.Strong,
                  fontWeight: 700,
                }}
              >
                {item.title}
              </CustomTypography>
            </Stack>
            <CustomTypography
              variant={"Body"}
              sx={{
                p: 0,
                color: theme.vars.palette.Content.Medium,
                fontWeight: 500,
                whiteSpace: "pre-wrap",
                wordBreak: "keep-all",
              }}
            >
              {renderInlineBold(item.content)}
            </CustomTypography>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );

  if (isChecking || isFetching || !analysisDetail) {
    return (
      <ContentTemplateV2 noPadding isWide>
        <Stack
          sx={{ width: "100%", minHeight: containerHeight }}
          alignItems="center"
          justifyContent="center"
        >
          <CircularProgress color="neutral" />
        </Stack>
      </ContentTemplateV2>
    );
  }

  return (
    <Stack
      sx={{
        paddingBottom: "60px",
      }}
    >
      {analysisSectionContent}
      <BottomNextButton
        disabled={false}
        onClick={handleNext}
        background={theme.vars.palette.Background.Level1}
        text={"다음"}
      />
    </Stack>
  );
}
